import { Injectable } from '@angular/core';
import { catchError, Observable, of, shareReplay } from 'rxjs';
import { IDictionaryEntry } from './';
import { DictionaryService } from './dictionary.service';

/**
 * DictionaryCacheService
 * Lightweight in-memory caching layer over DictionaryService.
 * Caches each dictionary stream (sorted array of IDictionaryEntry) with shareReplay(1).
 * On error the cache entry is evicted so subsequent calls can retry.
 */
@Injectable({ providedIn: 'root' })
export class DictionaryCacheService {
  private cache = new Map<string, Observable<IDictionaryEntry[]>>();

  constructor(private dictionary: DictionaryService) {}

  /**
   * Returns a cached observable for the named dictionary.
   * First caller triggers network load; subsequent subscribers share the result.
   * If an error occurs the cached observable is removed so a later call retries.
   */
  public get(name: string): Observable<IDictionaryEntry[]> {
    const key = name.toLowerCase();
    const existing = this.cache.get(key);
    if (existing) {
      return existing;
    }

    const stream = this.dictionary.getDictionary(name).pipe(
      // shareReplay ensures single fetch; buffer size 1 since we only need the latest full list
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError(() => {
        // Evict failed stream so a future call can retry
        this.cache.delete(key);
        return of([] as IDictionaryEntry[]);
      })
    );

    this.cache.set(key, stream);
    return stream;
  }

  /** Clears one cached dictionary (next request will refetch). */
  public invalidate(name: string): void {
    this.cache.delete(name.toLowerCase());
  }

  /** Clears all cached dictionaries. */
  public clear(): void {
    this.cache.clear();
  }
}
